import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import gsap from "gsap";
import { getProductCategories } from "../../api/productService";
import { AgastyaLogo } from "../../assets/styles/AgastyaLogo.tsx";
// Initial fallback options while categories load
const fallbackProductOptions = [
  { label: "Loading...", link: "#" },
];

export default function Navbar() {
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const location = useLocation();
  
  // Refs for animation
  const mobileMenuRef = useRef(null);
  const menuItemsRef = useRef([]);
  const hamburgerRef = useRef(null);
  menuItemsRef.current = [];

  
  // Add or remove refs
  const addToMenuItemsRef = (el) => {
    if (el && !menuItemsRef.current.includes(el)) {
      menuItemsRef.current.push(el);
    }
  };

  // Fetch categories for dropdown menu
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const data = await getProductCategories({
          page: 1,
          perPage: 20,
          search: '',
          sorting: 'asc'
        });
        
        const fetchedCategories = data.categories || data.data || [];
        // Create dropdown options from categories with both ID and name
        const categoryOptions = fetchedCategories.map(cat => {
          const categoryName = cat.category_name;
          const categoryId = cat._id || cat.id || cat.category_id;
          const categorySlug = categoryName.toLowerCase().replace(/\s+/g, '-');
          const categoryLink = `/products/${categorySlug}`;
             
          return {
            label: categoryName,
            link: categoryLink,
            id: categoryId,
            slug: categorySlug
          };
        });
        
        setCategories(categoryOptions);
      } catch (error) {
        console.error('Error fetching categories for navbar:', error);
        // Use fallback options on error
        setCategories(fallbackProductOptions);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Handle mobile menu animation
  useEffect(() => {
    if (!mobileMenuRef.current) return;

    const menuEl = mobileMenuRef.current;
    const hamburgerEl = hamburgerRef.current;

    if (mobileMenuOpen) {
      gsap.set(menuEl, { display: 'block', height: 'auto' });
      const height = menuEl.offsetHeight;
      gsap.set(menuEl, { height: 0 });
      gsap.to(menuEl, {
        height: height,
        duration: 0.5,
        ease: 'power3.out'
      });

      gsap.fromTo(menuItemsRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.1,
          duration: 0.4,
          ease: 'power2.out',
          delay: 0.2
        }
      );

      if (hamburgerEl) {
        gsap.to(hamburgerEl.querySelectorAll('rect'), {
          duration: 0.4, attr: { y: 8 }, ease: 'power2.inOut'
        });
        gsap.to(hamburgerEl.querySelector('rect:first-child'), {
          duration: 0.4, rotation: 45, transformOrigin: 'center', ease: 'power2.inOut'
        });
        gsap.to(hamburgerEl.querySelector('rect:last-child'), {
          duration: 0.4, rotation: -45, transformOrigin: 'center', ease: 'power2.inOut'
        });
        gsap.to(hamburgerEl.querySelector('rect:nth-child(2)'), {
          duration: 0.4, opacity: 0, ease: 'power2.inOut'
        });
      }
    } else {
      gsap.to(menuEl, {
        height: 0,
        duration: 0.5,
        ease: 'power3.out',
        onComplete: () => {
          setShowProductDropdown(false);
          if (menuEl) gsap.set(menuEl, { display: 'none' });
        }
      });

      if (hamburgerEl) {
        gsap.to(hamburgerEl.querySelector('rect:first-child'), {
          duration: 0.4, rotation: 0, attr: { y: 0 }, ease: 'power2.inOut'
        });
        gsap.to(hamburgerEl.querySelector('rect:nth-child(2)'), {
          duration: 0.4, opacity: 1, ease: 'power2.inOut'
        });
        gsap.to(hamburgerEl.querySelector('rect:last-child'), {
          duration: 0.4, rotation: 0, attr: { y: 16 }, ease: 'power2.inOut'
        });
      }
    }

    return () => {
      gsap.killTweensOf(menuEl);
      gsap.killTweensOf(menuItemsRef.current);
      if (hamburgerEl) gsap.killTweensOf(hamburgerEl.querySelectorAll('rect'));
    };
  }, [mobileMenuOpen]);
  
  // Effect to update menu height when dropdown opens/closes
  useEffect(() => {
    if (mobileMenuOpen && mobileMenuRef.current) {
      // Update height when dropdown state changes
      gsap.to(mobileMenuRef.current, {
        height: 'auto',
        duration: 0.3,
        ease: 'power2.out'
      });
    }
  }, [showProductDropdown, mobileMenuOpen]);

  // Handle menu item click
  const handleMenuItemClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="w-full relative z-50">
    <nav className="bg-gradient-to-r from-[#5b2c2a] via-[#b03a2e] to-[#f39c12] rounded-b-[20px] shadow-md">
      <div className="container-custom flex items-center justify-between px-[20px] md:px-4 py-[21px] md:py-4">
        {/* Logo */}
        <div className="flex items-center gap-2">
          {/* <img
            src={logo}
            alt="Agastya Logo"
            className="h-8 w-full max-w-[129px]"
          /> */}
          <Link to="/" className="inline-block" title="Agastya">
          <AgastyaLogo className="w-[105px] lg:w-[115px] xl:w-[134px] h-auto"/>
          </Link>
        </div>

        {/* Mobile menu button */}
        <button 
          className="lg:hidden text-white focus:outline-none" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          <svg 
            ref={hamburgerRef}
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none"
          >
            <rect x="2" y="0" width="20" height="2" rx="1" fill="white" />
            <rect x="2" y="8" width="20" height="2" rx="1" fill="white" />
            <rect x="2" y="16" width="20" height="2" rx="1" fill="white" />
          </svg>
        </button>

        {/* Desktop Menu */}
        <ul className="hidden lg:flex items-center gap-[6px] xl:gap-[15px] list-none m-0 p-0 text-white font-medium text-sm">
          <li className="relative">
            <Link 
              to="/" 
              className={`px-2 lg:px-3 xl:px-4 py-2 rounded-full no-underline font-medium transition-all duration-300 hover:scale-105 hover:shadow-md ${location.pathname === "/" 
                ? "bg-white text-black border-2 border-white" 
                : "text-white hover:bg-white/10"}`}
            >
              Home
            </Link>
          </li>
          <li className="relative">
            <Link 
              to="/about-us" 
              className={`px-4 py-2 rounded-full no-underline text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-md ${location.pathname === "/about-us" 
                ? "bg-white text-black border-2 border-white" 
                : "text-white hover:bg-white/10"}`}
            >
              About us
            </Link>
          </li>
          <li className="relative">
            <Link 
              to="/awards" 
              className={`px-4 py-2 rounded-full no-underline text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-md ${location.pathname === "/awards" 
                ? "bg-white text-black border-2 border-white" 
                : "text-white hover:bg-white/10"}`}
            >
              Awards & Testimonials
            </Link>
          </li>
          <li
            className="relative"
            onMouseEnter={() => setShowProductDropdown(true)}
            onMouseLeave={() => setShowProductDropdown(false)}
            onFocus={() => setShowProductDropdown(true)}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowProductDropdown(false); }}
          >
            <Link 
              to="/products" 
              className={`flex items-center px-4 py-2 rounded-full no-underline text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-md ${location.pathname.includes("/products") 
                ? "bg-white text-black border-2 border-white" 
                : "text-white hover:bg-white/10"}`}
            >
              Products
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </Link>
            {/* Dropdown */}
            {showProductDropdown && (
              <ul className="absolute top-full left-0 mt-2 bg-white text-black text-sm py-2 w-48 rounded-md shadow-lg z-10 list-none max-h-64 overflow-y-auto">
                {categoriesLoading ? (
                  <li className="block">
                    <span className="block px-4 py-2 text-gray-500 flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading categories...
                    </span>
                  </li>
                ) : categories.length > 0 ? (
                  categories.map((category) => (
                    <li key={category.label} className="block">
                      <Link 
                        to={category.link} 
                        className="block px-4 py-2 no-underline text-black hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          setShowProductDropdown(false);
                        }}
                      >
                        {category.label}
                      </Link>
                    </li>
                  ))
                ) : (
                  <li className="block">
                    <span className="block px-4 py-2 text-gray-500">No categories found</span>
                  </li>
                )}
              </ul>
            )}
          </li>
          <li>
            <Link 
              to="/manufacturing" 
              className={`px-4 py-2 rounded-full no-underline text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-md ${location.pathname === "/manufacturing" 
                ? "bg-white text-black border-2 border-white" 
                : "text-white hover:bg-white/10"}`}
            >
              Manufacturing
            </Link>
          </li>
          <li className="relative">
            <Link 
              to="/career" 
              className={`px-4 py-2 rounded-full no-underline text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-md ${location.pathname === "/career" 
                ? "bg-white text-black border-2 border-white" 
                : "text-white hover:bg-white/10"}`}
            >
              Career
            </Link>
          </li>
        </ul>

        {/* Contact Button */}
        <div className="hidden lg:flex pt-[2px] pb-[2px]">
            <Link 
              to="/contact" 
              className={`flex items-center gap-[6px] xl:gap-[13px] px-[14px] lg:px-[18px] xl:px-[26px] py-[10px] xl:py-[12px] rounded-full no-underline font-medium shadow transition-all duration-300 hover:scale-105 hover:shadow-md ${location.pathname === "/contact" 
                ? "bg-[linear-gradient(90deg,#593747_0%,#DB4B12_59%,#FFB603_100%)] text-white border-2 border-white" 
                : "bg-white text-black"}`}
            >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                <path d="M6.99103 8.97303C7.1029 9.02441 7.22893 9.03614 7.34836 9.00631C7.4678 8.97648 7.5735 8.90685 7.64807 8.80891L7.84036 8.55703C7.94127 8.42248 8.07212 8.31328 8.22255 8.23807C8.37298 8.16285 8.53885 8.1237 8.70703 8.1237H10.332C10.6193 8.1237 10.8949 8.23783 11.0981 8.441C11.3012 8.64416 11.4154 8.91971 11.4154 9.20703V10.832C11.4154 11.1193 11.3012 11.3949 11.0981 11.5981C10.8949 11.8012 10.6193 11.9154 10.332 11.9154C7.74617 11.9154 5.26622 10.8881 3.43774 9.05966C1.60926 7.23118 0.582031 4.75122 0.582031 2.16536C0.582031 1.87805 0.696168 1.6025 0.899332 1.39933C1.1025 1.19617 1.37805 1.08203 1.66536 1.08203H3.29036C3.57768 1.08203 3.85323 1.19617 4.0564 1.39933C4.25956 1.6025 4.3737 1.87805 4.3737 2.16536V3.79036C4.3737 3.95855 4.33454 4.12442 4.25933 4.27485C4.18411 4.42527 4.07491 4.55612 3.94036 4.65703L3.68686 4.84716C3.58742 4.92309 3.51733 5.0311 3.4885 5.15285C3.45967 5.27459 3.47387 5.40257 3.5287 5.51503C4.26898 7.01862 5.48651 8.23463 6.99103 8.97303Z" fill={location.pathname === "/contact" ? "white" : "#010101"} stroke={location.pathname === "/contact" ? "white" : "#010101"} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Contact us</span>   
            </Link>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        ref={mobileMenuRef} 
        className="lg:hidden main-gradiant overflow-hidden" 
        style={{ height: 0, display: 'none' }}
      >
        <div className="container-custom flex flex-col space-y-4 py-4">
          <Link 
            to="/" 
            ref={addToMenuItemsRef}
            onClick={handleMenuItemClick}
            className="text-white no-underline font-medium transition-colors hover:text-gray-100 py-2"
          >
            Home
          </Link>
          <Link 
            to="/about-us" 
            ref={addToMenuItemsRef}
            onClick={handleMenuItemClick}
            className="text-white no-underline font-medium transition-colors hover:text-gray-100 py-2"
          >
            About us
          </Link>
          <Link 
            to="/awards" 
            ref={addToMenuItemsRef}
            onClick={handleMenuItemClick}
            className="text-white no-underline font-medium transition-colors hover:text-gray-100 py-2"
          >
            Awards & Testimonials
          </Link>
                    {/* Mobile Products Dropdown */}
          <div className="relative" ref={addToMenuItemsRef}>
            <button 
              onClick={() => {
                setShowProductDropdown(!showProductDropdown);
              }}
              className="flex items-center text-white no-underline font-medium transition-colors hover:text-gray-100 focus:outline-none py-2 w-full text-left"
            >
              Products
              <svg xmlns="http://www.w3.org/2000/svg" className={`ml-1 h-4 w-4 transition-transform ${showProductDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div className={`mt-2 pl-4 flex flex-col space-y-2 overflow-y-auto transition-all duration-300 ${showProductDropdown ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
              {categoriesLoading ? (
                <span className="text-white/70 py-2 flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading categories...
                </span>
              ) : categories.length > 0 ? (
                categories.map((category) => (
                  <Link 
                    key={category.link}
                    to={category.link}
                    onClick={() => {
                      handleMenuItemClick();
                      setShowProductDropdown(false);
                    }}
                    className="text-white no-underline font-medium transition-colors hover:text-gray-100 py-2"
                  >
                    {category.label}
                  </Link>
                ))
              ) : (
                <span className="text-white/70 py-2">No categories found</span>
              )}
            </div>
          </div>
          
          <Link 
            to="/manufacturing" 
            ref={addToMenuItemsRef}
            onClick={handleMenuItemClick}
            className="text-white no-underline font-medium transition-colors hover:text-gray-100 py-2"
          >
            Manufacturing
          </Link>
          
          
          <Link 
            to="/career" 
            ref={addToMenuItemsRef}
            onClick={handleMenuItemClick}
            className="text-white no-underline font-medium transition-colors hover:text-gray-100 py-2"
          >
            Career
          </Link>
          
          {/* Mobile Contact Button */}
          <div className="pt-[2px] pb-[2px]" ref={addToMenuItemsRef}>
            <Link 
              to="/contact" 
              onClick={handleMenuItemClick}
              className="flex items-center gap-2 bg-white text-black px-4 py-3 rounded-full no-underline font-medium shadow w-fit"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none" aria-hidden="true">
                <path d="M6.99103 8.97303C7.1029 9.02441 7.22893 9.03614 7.34836 9.00631C7.4678 8.97648 7.5735 8.90685 7.64807 8.80891L7.84036 8.55703C7.94127 8.42248 8.07212 8.31328 8.22255 8.23807C8.37298 8.16285 8.53885 8.1237 8.70703 8.1237H10.332C10.6193 8.1237 10.8949 8.23783 11.0981 8.441C11.3012 8.64416 11.4154 8.91971 11.4154 9.20703V10.832C11.4154 11.1193 11.3012 11.3949 11.0981 11.5981C10.8949 11.8012 10.6193 11.9154 10.332 11.9154C7.74617 11.9154 5.26622 10.8881 3.43774 9.05966C1.60926 7.23118 0.582031 4.75122 0.582031 2.16536C0.582031 1.87805 0.696168 1.6025 0.899332 1.39933C1.1025 1.19617 1.37805 1.08203 1.66536 1.08203H3.29036C3.57768 1.08203 3.85323 1.19617 4.0564 1.39933C4.25956 1.6025 4.3737 1.87805 4.3737 2.16536V3.79036C4.3737 3.95855 4.33454 4.12442 4.25933 4.27485C4.18411 4.42527 4.07491 4.55612 3.94036 4.65703L3.68686 4.84716C3.58742 4.92309 3.51733 5.0311 3.4885 5.15285C3.45967 5.27459 3.47387 5.40257 3.5287 5.51503C4.26898 7.01862 5.48651 8.23463 6.99103 8.97303Z" fill="#010101" stroke="#010101" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Contact us</span>   
            </Link>
          </div>
        </div>
      </div>
    </nav>
    </header>
  );
}

